import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cnames from 'classnames'
import { useField, useFormikContext } from 'formik'
import { normalizeToArray } from 'Utils/Arrays'
import { licensePlatesRegex } from 'Shared/RegExpressions'
import './TagInputField.scss'

const TagInputField = ({ name, valueAs, placeholder, separators, maxTags, whitelist, enforceWhitelist = false,
                         onChange, ...props }) => {
  const inputRef = useRef(null)
  let field
  let meta
  let formik
  try {
    [field, meta] = useField(name)
  } catch (e) {
    field = { value: props.defaultValue || '' }
    meta = { touched: false, error: undefined }
  }
  try {
    formik = useFormikContext()
  } catch (e) {
    formik = null
  }
  const hasError = meta?.touched && meta?.error
  const inputAreaClasses = cnames('tag-input-input-area d-flex flex-wrap align-items-center px-3 py-2',
                                  'border-1 rounded-2', { 'tag-input-input-area-error': hasError })

  const setFieldValue = formik && typeof formik.setFieldValue === 'function' ? formik.setFieldValue : null
  const setFieldTouched = formik && typeof formik.setFieldTouched === 'function' ? formik.setFieldTouched : null

  const [tags, setTags] = useState(normalizeToArray(field.value))
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    const next = normalizeToArray(field.value)
    const changed = next.length !== tags.length || next.some((t, i) => String(t) !== String(tags[i]))
    if (changed) setTags(next)
  }, [field.value])

  const commitTags = nextTags => {
    setTags(nextTags)
    const newValue = valueAs === 'string' ? nextTags.join(',') : nextTags
    if (setFieldValue) setFieldValue(name, newValue)

    if (typeof onChange === 'function') onChange(newValue)
  }

  const isAllowed = candidate => {
    if (!candidate || String(candidate).trim() === '') return false
    if (!whitelist || !Array.isArray(whitelist) || whitelist.length === 0) return true

    const found = whitelist.some(w => String(w).toLowerCase() === String(candidate).toLowerCase())
    return found
  }

  const matchesRegex = candidate => licensePlatesRegex.test(candidate)

  const addTag = raw => {
    if (!raw && raw !== 0) return
    const sepRegex = new RegExp(`[${separators.map(s => `\\${s}`).join('')}]`)
    let pieces = typeof raw === 'string' ? raw.split(sepRegex) : [String(raw)]
    pieces = pieces.map(p => p.trim()).filter(Boolean)
    if (pieces.length === 0) return

    const next = [...tags]
    pieces.forEach(p => {
      if ((maxTags && next.length >= maxTags) || (enforceWhitelist && !isAllowed(p))) return
      if (next.some(t => String(t).toLowerCase() === String(p).toLowerCase())) return

      next.push(p.toUpperCase())
    })

    if (next.length !== tags.length) commitTags(next)
  }

  const removeTagAt = index => {
    if (index < 0 || index >= tags.length) return
    const next = tags.slice(0, index).concat(tags.slice(index + 1))
    commitTags(next)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (inputValue.trim().length === 0) return

      addTag(inputValue)
      setInputValue('')
    }

    if (separators.includes(e.key)) {
      e.preventDefault()
      if (inputValue.trim().length === 0) return

      addTag(inputValue)
      setInputValue('')
    }

    if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) removeTagAt(tags.length - 1)
  }

  const handlePaste = e => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text')
    if (!pasted) return

    const sepRegex = new RegExp(`[${separators.map(s => `\\${s}`).join('')}]`)
    if (sepRegex.test(pasted)) {
      e.preventDefault()
      addTag(pasted)
      setInputValue('')
    }
  }

  const handleBlur = () => {
    if (inputValue.trim() !== '') {
      addTag(inputValue)
      setInputValue('')
    }
    if (setFieldTouched) setFieldTouched(name, true, true)
  }

  const handleRemoveClick = idx => e => {
    e.preventDefault()
    removeTagAt(idx)
    if (inputRef.current) inputRef.current.focus()
  }

  const focusInput = () => {
    if (inputRef.current) inputRef.current.focus()
  }

  const handleContainerKeyDown = e => {
    const { key } = e || {}
    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      e.preventDefault()
      focusInput()
    }
  }

  const handleContainerMouseDown = e => {
    e.preventDefault()
    focusInput()
  }

  const handleContainerTouchStart = () => {
    focusInput()
  }

  return (
    <>
      <label htmlFor={name} className="form-label">License plate</label>

      <div className="tag-input-wrapper" {...props}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div className={inputAreaClasses} role="group" onClick={focusInput} onKeyDown={handleContainerKeyDown}
             onMouseDown={handleContainerMouseDown} onTouchStart={handleContainerTouchStart}
             aria-label={props['aria-label'] || name}>
          {tags.map((tag, i) => (
            <span className={`tag-input-tag ${!matchesRegex(tag) && 'tag-input-tag-invalid'}`} key={String(tag)}>
              <span className="tag-input-tag-text">{tag}</span>
              <button className={`tag-input-remove ${matchesRegex(tag) ? 'text-white' : 'text-danger'}`}
                      onClick={handleRemoveClick(i)} type="button" aria-label={`Remove ${tag}`}>
                &times;
              </button>
            </span>
          ))}

          <input ref={inputRef} className="tag-input-input" id={name} value={inputValue}
                 onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste}
                 onBlur={handleBlur} placeholder={tags.length === 0 ? placeholder : ''}
                 aria-describedby={meta && meta.error ? `${name}-error` : undefined} />
        </div>

        {hasError && (
          <div id={`${name}-error`} className="tag-input-error">
            {meta.error}
          </div>
        )}
      </div>
    </>
  )
}

TagInputField.propTypes = {
  name: PropTypes.string.isRequired,
  valueAs: PropTypes.oneOf(['array', 'string']),
  placeholder: PropTypes.string,
  separators: PropTypes.arrayOf(PropTypes.string),
  maxTags: PropTypes.number,
  whitelist: PropTypes.arrayOf(PropTypes.string),
  enforceWhitelist: PropTypes.bool,
  onChange: PropTypes.func,
}

TagInputField.defaultProps = {
  valueAs: 'array',
  placeholder: 'Add a tag and press Enter',
  separators: [','],
  maxTags: undefined,
  whitelist: undefined,
  enforceWhitelist: false,
  onChange: undefined,
}

export default TagInputField
